import { apiClient } from './api-client';

export const adminHubService = {
  getGlobalDashboard: () => apiClient.get('/api/admin/hub/global-dashboard'),
  getBusinessAnalytics: (days = 30) =>
    apiClient.get(`/api/admin/hub/business-analytics?days=${days}`),

  listSubscriptions: (page = 1, status?: string) => {
    const q = new URLSearchParams({ page: String(page), limit: '25' });
    if (status) q.set('status', status);
    return apiClient.get(`/api/admin/hub/subscriptions?${q}`);
  },
  updateSubscription: (id: number, body: Record<string, unknown>) =>
    apiClient.patch(`/api/admin/hub/subscriptions/${id}`, body),

  listFeatureFlags: () => apiClient.get('/api/admin/hub/feature-flags'),
  updateFeatureFlag: (key: string, body: Record<string, unknown>) =>
    apiClient.put(`/api/admin/hub/feature-flags/${encodeURIComponent(key)}`, body),

  listModeration: (page = 1, status = 'pending') =>
    apiClient.get(`/api/admin/hub/moderation?page=${page}&limit=25&status=${status}`),
  resolveModeration: (id: number, body: Record<string, unknown>) =>
    apiClient.patch(`/api/admin/hub/moderation/${id}`, body),

  verificationQueue: (page = 1) =>
    apiClient.get(`/api/admin/hub/verification-queue?page=${page}&limit=25`),

  listDisputes: (page = 1, status?: string) => {
    const q = new URLSearchParams({ page: String(page), limit: '25' });
    if (status) q.set('status', status);
    return apiClient.get(`/api/admin/hub/disputes?${q}`);
  },
  createDispute: (body: Record<string, unknown>) =>
    apiClient.post('/api/admin/hub/disputes', body),
  updateDispute: (id: string, body: Record<string, unknown>) =>
    apiClient.patch(`/api/admin/hub/disputes/${id}`, body),

  listReferrals: (params: {
    page?: number;
    limit?: number;
    status?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
    referrerId?: number;
    credited?: boolean;
  } = {}) => {
    const q = new URLSearchParams();
    q.set('page', String(params.page ?? 1));
    q.set('limit', String(params.limit ?? 25));
    if (params.status) q.set('status', params.status);
    if (params.startDate) q.set('startDate', params.startDate);
    if (params.endDate) q.set('endDate', params.endDate);
    if (params.search) q.set('search', params.search);
    if (params.referrerId) q.set('referrerId', String(params.referrerId));
    if (params.credited === true) q.set('credited', 'true');
    if (params.credited === false) q.set('credited', 'false');
    return apiClient.get(`/api/admin/hub/referrals?${q}`);
  },
  referralsSummary: (params: {
    status?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
    referrerId?: number;
    credited?: boolean;
  } = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.startDate) q.set('startDate', params.startDate);
    if (params.endDate) q.set('endDate', params.endDate);
    if (params.search) q.set('search', params.search);
    if (params.referrerId) q.set('referrerId', String(params.referrerId));
    if (params.credited === true) q.set('credited', 'true');
    if (params.credited === false) q.set('credited', 'false');
    return apiClient.get(`/api/admin/hub/referrals/summary?${q}`);
  },
  referralsLeaderboard: (params: {
    status?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
  } = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.startDate) q.set('startDate', params.startDate);
    if (params.endDate) q.set('endDate', params.endDate);
    q.set('limit', String(params.limit ?? 25));
    return apiClient.get(`/api/admin/hub/referrals/leaderboard?${q}`);
  },

  listCreatorRewards: (params: {
    page?: number;
    limit?: number;
    search?: string;
    eligibility?: string;
    credited?: boolean;
    aboveThreshold?: boolean;
  } = {}) => {
    const q = new URLSearchParams();
    q.set('page', String(params.page ?? 1));
    q.set('limit', String(params.limit ?? 25));
    if (params.search) q.set('search', params.search);
    if (params.eligibility) q.set('eligibility', params.eligibility);
    if (params.credited === true) q.set('credited', 'true');
    if (params.credited === false) q.set('credited', 'false');
    if (params.aboveThreshold === true) q.set('aboveThreshold', 'true');
    if (params.aboveThreshold === false) q.set('aboveThreshold', 'false');
    return apiClient.get(`/api/admin/hub/creator-rewards?${q}`);
  },
  creatorRewardsSummary: (params: {
    search?: string;
    eligibility?: string;
    credited?: boolean;
    aboveThreshold?: boolean;
  } = {}) => {
    const q = new URLSearchParams();
    if (params.search) q.set('search', params.search);
    if (params.eligibility) q.set('eligibility', params.eligibility);
    if (params.credited === true) q.set('credited', 'true');
    if (params.credited === false) q.set('credited', 'false');
    if (params.aboveThreshold === true) q.set('aboveThreshold', 'true');
    if (params.aboveThreshold === false) q.set('aboveThreshold', 'false');
    return apiClient.get(`/api/admin/hub/creator-rewards/summary?${q}`);
  },
  markCreatorRewardPaid: (userId: number, notes?: string) =>
    apiClient.post(`/api/admin/hub/creator-rewards/${userId}/mark-paid`, {
      ...(notes ? { notes } : {}),
    }),

  listCms: () => apiClient.get('/api/admin/hub/cms'),
  createCms: (body: Record<string, unknown>) => apiClient.post('/api/admin/hub/cms', body),
  updateCms: (id: string, body: Record<string, unknown>) =>
    apiClient.put(`/api/admin/hub/cms/${id}`, body),

  listCategories: () => apiClient.get('/api/admin/hub/categories'),
  createCategory: (body: Record<string, unknown>) =>
    apiClient.post('/api/admin/hub/categories', body),
  updateCategory: (uuid: string, body: Record<string, unknown>) =>
    apiClient.put(`/api/admin/hub/categories/${uuid}`, body),

  getCommission: () => apiClient.get('/api/admin/hub/commission'),
  updateCommission: (chargeKey: string, body: Record<string, unknown>) =>
    apiClient.put(`/api/admin/hub/commission/${encodeURIComponent(chargeKey)}`, body),

  listAppSettings: () => apiClient.get('/api/admin/hub/app-settings'),
  updateAppSetting: (key: string, body: Record<string, unknown>) =>
    apiClient.put(`/api/admin/hub/app-settings/${encodeURIComponent(key)}`, body),
};
